import React from 'react';
import { css } from '@emotion/react';
import { Grid, MenuItem, TextField, InputAdornment } from '@material-ui/core';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import { Card, H1, Body1 } from './blocks';

import FuelBucketEditor from './FuelBucketEditor';
import QboTagField from './QboTagField';

const FSC_MODE_OPTIONS = [
    { value: 'MODIFIER', label: 'Modifier (% of subtotal, fuel-price-indexed)' },
    { value: 'FLAT', label: 'Flat ($ per order)' },
    { value: 'MPG', label: 'Miles × MPG ($ per gallon)' },
    { value: 'BY_ITEM', label: 'Per Item ($ × qty, fuel-price-indexed)' },
    { value: 'BY_MILE', label: 'Per Mile ($ × miles, fuel-price-indexed)' },
    { value: 'BY_CABINET', label: 'Per Cabinet ($ × cabinet qty, fuel-price-indexed)' },
];

// Flat mode: a single $ per-order surcharge. Stored as one FUEL_PRICE_FLAT row.
const FlatEditor = ({ rates, callbacks, qbo_tags }) => {
    const row = rates?.['FUEL_PRICE_FLAT']?.[0];
    return (
        <Grid
            container
            direction="column"
            css={css`
                gap: 16px;
                max-width: 400px;
            `}
        >
            <Body1>Charge a fixed dollar amount per order, regardless of fuel price.</Body1>
            <CurrencyTextField
                label="Flat FSC per order"
                variant="outlined"
                value={row?.rate || 0}
                onChange={(_, val) => {
                    callbacks.setSingleRate({ type: 'FUEL_PRICE_FLAT', rate: val });
                }}
                InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
            />
            <QboTagField
                options={qbo_tags}
                value={row?.qbo_tag}
                onCommit={(qbo_tag) => {
                    callbacks.setSingleRate({ type: 'FUEL_PRICE_FLAT', qbo_tag });
                }}
            />
        </Grid>
    );
};

// MPG mode: charge = fuelPrice × (miles / mpg). Stored as one FUEL_PRICE_MPG row whose
// `rate` field holds the vehicle's MPG. Backend computes the rest at price time.
const MpgEditor = ({ rates, callbacks, qbo_tags }) => {
    const row = rates?.['FUEL_PRICE_MPG']?.[0];
    return (
        <Grid
            container
            direction="column"
            css={css`
                gap: 16px;
                max-width: 400px;
            `}
        >
            <Body1>
                Charge based on miles driven and your vehicle's MPG. Backend computes fuelPrice × (miles ÷ MPG) per
                order.
            </Body1>
            <TextField
                label="Vehicle MPG"
                variant="outlined"
                type="number"
                value={row?.rate || ''}
                onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    callbacks.setSingleRate({
                        type: 'FUEL_PRICE_MPG',
                        rate: Number.isFinite(val) ? val : 0,
                    });
                }}
            />
            <QboTagField
                options={qbo_tags}
                value={row?.qbo_tag}
                onCommit={(qbo_tag) => {
                    callbacks.setSingleRate({ type: 'FUEL_PRICE_MPG', qbo_tag });
                }}
            />
        </Grid>
    );
};

const FuelRates = ({ className, rates, fsc_mode = 'MODIFIER', errors, callbacks, qbo_tags = [] }) => {
    const handleModeChange = (e) => {
        callbacks.updateTariff({ fsc_mode: e.target.value });
    };

    const knownMode = FSC_MODE_OPTIONS.find((o) => o.value === fsc_mode) ? fsc_mode : 'MODIFIER';

    return (
        <Card className={className}>
            <Grid
                container
                direction="row"
                css={css`
                    margin-bottom: 16px;
                    flex: 0;
                    flex-basis: 0;
                    align-items: center;
                    gap: 16px;
                `}
            >
                <H1>Fuel Surcharge</H1>
                <TextField
                    select
                    size="small"
                    label="Mode"
                    value={knownMode}
                    onChange={handleModeChange}
                    css={css`
                        min-width: 320px;
                    `}
                >
                    {FSC_MODE_OPTIONS.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                            {label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>

            {knownMode === 'MODIFIER' && (
                <FuelBucketEditor
                    type="FUEL_PRICE_MODIFIER"
                    isPercentage
                    fuelModalMode="MODIFIER"
                    rates={rates}
                    callbacks={callbacks}
                    qbo_tags={qbo_tags}
                />
            )}
            {knownMode === 'FLAT' && <FlatEditor rates={rates} callbacks={callbacks} qbo_tags={qbo_tags} />}
            {knownMode === 'MPG' && <MpgEditor rates={rates} callbacks={callbacks} qbo_tags={qbo_tags} />}
            {knownMode === 'BY_ITEM' && (
                <FuelBucketEditor
                    type="FUEL_PRICE_BY_ITEM"
                    fuelModalMode="BY_ITEM"
                    rates={rates}
                    callbacks={callbacks}
                    qbo_tags={qbo_tags}
                />
            )}
            {knownMode === 'BY_MILE' && (
                <FuelBucketEditor
                    type="FUEL_PRICE_BY_MILE"
                    fuelModalMode="BY_MILE"
                    rates={rates}
                    callbacks={callbacks}
                    qbo_tags={qbo_tags}
                />
            )}
            {knownMode === 'BY_CABINET' && (
                <FuelBucketEditor
                    type="FUEL_PRICE_BY_CABINET"
                    fuelModalMode="BY_CABINET"
                    rates={rates}
                    callbacks={callbacks}
                    qbo_tags={qbo_tags}
                />
            )}
        </Card>
    );
};

export default FuelRates;
